import CheckCircle from '@mui/icons-material/CheckCircle';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Search from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import {
  CARD_SX,
  DIVIDER,
  FilterChip,
  MISSING_SCORE,
  MissingScore,
  NetworkIcon,
  OutlineButton,
  ScoreValue,
  TEXT_SOFT,
  TEXT_STRONG,
} from './FlightCheckParts.js';
import {
  type AnalysisView,
  type BatchSummary,
  type FlightCheckCopy,
  fillQuery,
  type LatestBatchStrip as LatestBatchStripData,
  type NetworkRow,
} from './flightCheckView.js';
import {
  formatRanAt,
  formatScore,
  MAX_SCORE,
  ScoreThresholdsContext,
  scoreBand,
  scoreText,
  useScoreThresholds,
} from './scoreScale.js';

interface FlightCheckAnalysisProps {
  data: AnalysisView;
  copy: FlightCheckCopy;
  search: string;
  onSearchChange: (search: string) => void;
  onViewBatch: (batchId: string) => void;
}

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns:
    'minmax(170px, 250px) repeat(2, minmax(80px, 120px)) minmax(90px, 130px) repeat(2, minmax(100px, 170px)) minmax(60px, 90px) 1fr',
  columnGap: '8px',
  alignItems: 'center',
  px: '16px',
};

const STAT_LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.66px',
  textTransform: 'uppercase',
  color: TEXT_SOFT,
  lineHeight: 'normal',
  whiteSpace: 'nowrap',
};

function ScoreBar({ score, width }: { score: number; width: number }): ReactNode {
  const theme = useTheme();
  const thresholds = useScoreThresholds();
  return (
    <Box
      role="progressbar"
      aria-valuenow={score}
      aria-valuemin={0}
      aria-valuemax={MAX_SCORE}
      sx={{
        width,
        height: 6,
        borderRadius: '3px',
        bgcolor: DIVIDER,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: `${(score / MAX_SCORE) * 100}%`,
          height: '100%',
          borderRadius: '3px',
          bgcolor: theme.palette.score[scoreBand(score, thresholds)].main,
        }}
      />
    </Box>
  );
}

function StatDivider(): ReactNode {
  return <Box sx={{ width: '1px', height: 48, bgcolor: DIVIDER, flexShrink: 0 }} />;
}

function Stat({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <Stack sx={{ gap: '4px', flexShrink: 0 }}>
      <Typography sx={STAT_LABEL_SX}>{label}</Typography>
      <Stack direction="row" sx={{ alignItems: 'baseline', gap: '6px' }}>
        {children}
      </Stack>
    </Stack>
  );
}

function ViewBatchLink({ onClick }: { onClick: () => void }): ReactNode {
  return (
    <OutlineButton onClick={onClick}>
      View batch
      <ChevronRight sx={{ fontSize: 16, ml: '2px', mr: '-4px' }} />
    </OutlineButton>
  );
}

function SubScore({
  score,
  size,
  copy,
}: {
  score: number | null;
  size: number;
  copy: FlightCheckCopy;
}): ReactNode {
  return score === null ? (
    <MissingScore reason={copy.missingSubScore} size={size} />
  ) : (
    <ScoreValue score={score} size={size} />
  );
}

function LatestBatchStrip({
  latest,
  copy,
  onViewBatch,
}: {
  latest: LatestBatchStripData;
  copy: FlightCheckCopy;
  onViewBatch: (batchId: string) => void;
}): ReactNode {
  return (
    <Stack
      data-testid="latest-batch"
      sx={{ ...CARD_SX, borderRadius: '16px', px: '25px', pt: '19px', pb: '21px', gap: '18px' }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <CheckCircle sx={{ fontSize: 24, color: 'score.high.main' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: TEXT_STRONG, flexShrink: 0 }}>
            Latest batch
          </Typography>
          <Typography noWrap sx={{ fontSize: 13, color: TEXT_SOFT }}>
            <Box component="span" sx={{ fontWeight: 500, color: TEXT_STRONG }}>
              {latest.name}
            </Box>
            {` · ${formatRanAt(latest.ranAt)} · ${latest.creativeCount} creatives analyzed`}
          </Typography>
        </Stack>
        <ViewBatchLink onClick={() => onViewBatch(latest.id)} />
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: '36px', flexWrap: 'wrap', rowGap: '16px' }}
      >
        <Stack sx={{ gap: '6px', width: 140 }}>
          <Stack direction="row" sx={{ alignItems: 'baseline', gap: '4px' }}>
            <ScoreValue score={latest.overall} size={34} />
            <Typography sx={{ fontSize: 13, color: TEXT_SOFT }}>/ 10</Typography>
          </Stack>
          <ScoreBar score={latest.overall} width={140} />
        </Stack>
        <StatDivider />
        <Stat label={copy.bestPractice}>
          <SubScore score={latest.bestPractice} size={22} copy={copy} />
        </Stat>
        <Stat label={copy.performance}>
          <SubScore score={latest.performance} size={22} copy={copy} />
        </Stat>
        <Stat label={copy.brandGuidelines}>
          <SubScore score={latest.brandGuidelines} size={22} copy={copy} />
        </Stat>
        <StatDivider />
        <Stat label={copy.weakestCategory}>
          {latest.weakest === null ? (
            <Typography sx={{ fontSize: 22, color: TEXT_SOFT, lineHeight: 'normal' }}>
              {MISSING_SCORE}
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 500,
                  color: 'score.low.text',
                  lineHeight: 'normal',
                }}
              >
                {`${latest.weakest.percent}%`}
              </Typography>
              <Typography sx={{ fontSize: 13, color: TEXT_STRONG }}>
                {latest.weakest.name}
              </Typography>
            </>
          )}
        </Stat>
        <Stack sx={{ gap: '4px', flexShrink: 0 }}>
          <Typography sx={STAT_LABEL_SX}>By platform</Typography>
          <Stack direction="row" sx={{ gap: '14px' }}>
            {latest.networks.map(({ network, score }) => (
              <Stack key={network.label} direction="row" sx={{ alignItems: 'center', gap: '5px' }}>
                <NetworkIcon network={network} size={20} />
                <ScoreValue score={score} size={15} weight={600} />
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Stack sx={{ gap: '4px', flexShrink: 0 }}>
          <Typography sx={STAT_LABEL_SX}>Needs attention</Typography>
          <Box
            component="span"
            sx={{
              alignSelf: 'flex-start',
              px: '8px',
              py: '3px',
              borderRadius: '10px',
              bgcolor: 'score.low.bg',
              color: 'score.low.text',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.4px',
              lineHeight: 'normal',
            }}
          >
            {`${latest.needsAttention} CREATIVES`}
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
}

function PlatformRow({ row, copy }: { row: NetworkRow; copy: FlightCheckCopy }): ReactNode {
  const theme = useTheme();
  const thresholds = useScoreThresholds();
  const metric = (value: number | null): ReactNode =>
    value === null ? (
      <Box>
        <MissingScore reason={copy.missingSubScore} size={14} />
      </Box>
    ) : (
      <Typography
        sx={{ fontSize: 14, fontWeight: 500, color: scoreText(theme, value, thresholds) }}
      >
        {formatScore(value)}
      </Typography>
    );
  return (
    <Box
      data-testid="platform-row"
      sx={{ ...GRID_SX, minHeight: 55, py: '8px', borderTop: `1px solid ${DIVIDER}` }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: '8px' }}>
        <NetworkIcon network={row.network} size={22} />
        <ScoreValue score={row.score} size={15} weight={600} />
        <ScoreBar score={row.score} width={90} />
      </Stack>
      {metric(row.bestPractice)}
      {metric(row.performance)}
      {metric(row.brandGuidelines)}
      <Typography sx={{ fontSize: 13, color: TEXT_STRONG }}>
        {row.weakest === null ? MISSING_SCORE : row.weakest.name}
        {row.weakest === null ? null : (
          <Box component="span" sx={{ ml: '8px', color: TEXT_SOFT }}>
            {`${row.weakest.percent}%`}
          </Box>
        )}
      </Typography>
      <Box sx={{ position: 'relative', width: 48, height: 40 }}>
        <Box
          {...(row.lowestCreative.thumbnailUrl === null
            ? { role: 'img', 'aria-label': `${copy.lowestCreative} · ${row.network.label}` }
            : {
                component: 'img',
                src: row.lowestCreative.thumbnailUrl,
                alt: `${copy.lowestCreative} · ${row.network.label}`,
              })}
          sx={{
            width: 40,
            height: 38,
            objectFit: 'cover',
            borderRadius: '6px',
            display: 'block',
            bgcolor: 'background.page',
          }}
        />
        <Box
          component="span"
          sx={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            px: '5px',
            py: '2px',
            borderRadius: '6px',
            bgcolor: 'background.paper',
            border: `1px solid ${DIVIDER}`,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 'normal',
            color: scoreText(theme, row.lowestCreative.score, thresholds),
          }}
        >
          {formatScore(row.lowestCreative.score)}
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13, color: TEXT_STRONG }}>{row.type ?? MISSING_SCORE}</Typography>
    </Box>
  );
}

function BatchGroup({
  batch,
  copy,
  onViewBatch,
}: {
  batch: BatchSummary;
  copy: FlightCheckCopy;
  onViewBatch: (batchId: string) => void;
}): ReactNode {
  return (
    <Box data-testid="batch-group">
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 52,
          px: '16px',
          mt: '4px',
          bgcolor: 'background.page',
          borderRadius: '8px',
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: '10px', minWidth: 0 }}>
          <Typography
            noWrap
            title={batch.name}
            sx={{ fontSize: 14, fontWeight: 600, color: TEXT_STRONG }}
          >
            {batch.name}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12, color: TEXT_SOFT }}>
            {formatRanAt(batch.ranAt)}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: '16px', flexShrink: 0 }}>
          {batch.statusLabel === null ? (
            <>
              <Typography sx={{ fontSize: 12, color: TEXT_SOFT }}>
                {`${batch.creativeCount} creatives analyzed`}
              </Typography>
              <ViewBatchLink onClick={() => onViewBatch(batch.id)} />
            </>
          ) : (
            <Typography
              data-testid="batch-status"
              sx={{ fontSize: 12, fontWeight: 600, color: TEXT_SOFT }}
            >
              {batch.statusLabel}
            </Typography>
          )}
        </Stack>
      </Stack>
      {batch.statusLabel === null
        ? batch.rows.map((row) => <PlatformRow key={row.network.label} row={row} copy={copy} />)
        : null}
    </Box>
  );
}

function AllBatches({
  batches,
  copy,
  search,
  onSearchChange,
  onViewBatch,
}: Omit<FlightCheckAnalysisProps, 'data'> & { batches: BatchSummary[] }): ReactNode {
  const columns = [
    'Platform / score',
    copy.bestPractice,
    copy.performance,
    copy.brandGuidelines,
    copy.weakestCategory,
    copy.lowestCreative,
    'Type',
  ];
  const query = search.trim();

  return (
    <Stack
      sx={{ ...CARD_SX, borderRadius: '16px', px: '25px', pt: '21px', pb: '21px', gap: '16px' }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 500, color: TEXT_STRONG }}>
        All batches
      </Typography>
      <Stack direction="row" sx={{ gap: '12px', flexWrap: 'wrap' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: '8px',
            width: 320,
            maxWidth: '100%',
            height: 34,
            px: '13px',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: '9px',
          }}
        >
          <Search sx={{ fontSize: 16, color: 'text.muted' }} />
          <InputBase
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search by batch name"
            inputProps={{ 'aria-label': 'Search by batch name' }}
            sx={{ flex: 1, fontSize: 12, color: TEXT_STRONG }}
          />
        </Stack>
        <FilterChip label="funnel stage" value={copy.allStages} />
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1080 }}>
          <Box sx={{ ...GRID_SX, height: 34 }}>
            {columns.map((column) => (
              <Typography key={column} sx={STAT_LABEL_SX}>
                {column}
              </Typography>
            ))}
          </Box>
          {batches.length === 0 ? (
            <Typography sx={{ px: '16px', py: '24px', fontSize: 13, color: TEXT_SOFT }}>
              {query === '' ? copy.noMatches : fillQuery(copy.noSearchMatches, query)}
            </Typography>
          ) : (
            batches.map((batch) => (
              <BatchGroup key={batch.id} batch={batch} copy={copy} onViewBatch={onViewBatch} />
            ))
          )}
        </Box>
      </Box>
    </Stack>
  );
}

export function FlightCheckAnalysis({
  data,
  copy,
  search,
  onSearchChange,
  onViewBatch,
}: FlightCheckAnalysisProps): ReactNode {
  return (
    <ScoreThresholdsContext.Provider value={data.thresholds}>
      <Stack sx={{ gap: '32px', minWidth: 0 }}>
        {data.latest === null ? null : (
          <LatestBatchStrip latest={data.latest} copy={copy} onViewBatch={onViewBatch} />
        )}
        <AllBatches
          batches={data.batches}
          copy={copy}
          search={search}
          onSearchChange={onSearchChange}
          onViewBatch={onViewBatch}
        />
      </Stack>
    </ScoreThresholdsContext.Provider>
  );
}
